/**
 * The faces of the game's tiles and cards: forum tiles, extra-action tiles,
 * construction tiles, Trajan tiles, bonus tiles and commodity cards. Each is
 * drawn with its top-left at the origin, at the size it is asked for, so the
 * board, the tableau and the tooltips can all use the same face.
 */
import type {
  BonusTile,
  CommodityCard,
  ConstructionTile,
  ExtraActionTile,
  ForumTile,
  TrajanTile,
} from '@trajan/engine';
import { Container, Graphics } from 'pixi.js';
import { INK, INK_SOFT, TILE, mix, type Paint } from '../palette';
import { icon } from './icons';
import { actionMarker, cardFace, label, tile } from './pieces';

/** The ink each demand is drawn in, so bread, games and religion part at a glance. */
export const DEMAND_INK: Record<string, number> = {
  bread: 0x8a5424,
  games: 0x3d5a7a,
  helmet: 0x3d5a7a,
  religion: 0xb4471c,
  flame: 0xb4471c,
};

const put = <T extends Container>(parent: Container, child: T, x: number, y: number): T => {
  child.position.set(x, y);
  parent.addChild(child);
  return child;
};

/** A forum tile: a demand, senate votes, or one of the four wilds. */
export function forumTileFace(t: ForumTile, size: number): Container {
  const c = new Container();
  const s = size / 56;
  switch (t.kind) {
    case 'demand': {
      c.addChild(tile(size, size, TILE.demand));
      put(c, icon(t.icon, 34 * s, DEMAND_INK[t.icon] ?? INK), size / 2, size / 2);
      return c;
    }
    case 'senate': {
      c.addChild(tile(size, size, TILE.senate));
      put(
        c,
        icon('laurel', 44 * s, mix(TILE.senate.dark, 0x000000, 0.1)),
        size / 2,
        size / 2 + 2 * s,
      );
      put(
        c,
        label(String(t.votes), 20 * s, { face: 'display', fill: INK }),
        size / 2,
        size / 2 - 3 * s,
      );
      put(c, label('votes', 9 * s, { fill: INK_SOFT, weight: '700' }), size / 2, size / 2 + 14 * s);
      return c;
    }
    default: {
      c.addChild(tile(size, size, TILE.wild));
      const inner: Record<string, string> = {
        'wild-commodity': 'vase',
        'wild-demand': 'bread',
        'wild-construction': 'door',
        'wild-extra-action': 'hourglass',
      };
      put(c, icon('wild', 20 * s, mix(TILE.wild.dark, 0x000000, 0.2)), 13 * s, 13 * s);
      put(
        c,
        icon(inner[t.kind] ?? 'star', 26 * s, mix(TILE.wild.dark, 0x000000, 0.35)),
        size / 2 + 5 * s,
        size / 2 + 5 * s,
      );
      return c;
    }
  }
}

/** A yellow extra-action tile, showing the action it repeats. */
export function extraTileFace(t: Pick<ExtraActionTile, 'action'>, size: number): Container {
  const c = new Container();
  const s = size / 56;
  c.addChild(tile(size, size, TILE.extra));
  put(c, icon(t.action, 30 * s, mix(TILE.extra.dark, 0x000000, 0.3)), size / 2, size / 2 - 3 * s);
  put(
    c,
    label('+1', 11 * s, { face: 'display', fill: mix(TILE.extra.dark, 0x000000, 0.3) }),
    size / 2,
    size - 9 * s,
  );
  return c;
}

/** A construction tile: the building, and its points in the corner. */
export function buildingFace(
  t: Pick<ConstructionTile, 'icon' | 'vp'>,
  w: number,
  h: number,
): Container {
  const c = new Container();
  c.addChild(tile(w, h, TILE.building));
  const k = Math.min(w, h);
  put(c, icon(t.icon, k * 0.56, mix(TILE.building.dark, 0x000000, 0.35)), w * 0.42, h / 2);
  put(
    c,
    label(String(t.vp), k * 0.34, { face: 'display', fill: 0xfff4e0, halo: TILE.building.dark }),
    w - k * 0.2,
    k * 0.24,
  );
  return c;
}

const TRAJAN_ICON: Record<string, string> = {
  cards: 'cards',
  vp: 'laurel',
  workers: 'hammer',
  legionnaires: 'spear',
};

/** A Trajan tile: the two marker colours it needs, its points, and its reward. */
export function trajanFace(t: TrajanTile, size: number, shadow = true): Container {
  const c = new Container();
  const s = size / 74;
  c.addChild(tile(size, size, TILE.trajan, { shadow }));
  const gem = 17 * s;
  put(c, actionMarker(t.colors[0], gem, false), size / 2 - gem * 0.62, 14 * s);
  put(c, actionMarker(t.colors[1], gem, false), size / 2 + gem * 0.62, 14 * s);
  put(c, label(String(t.vp), 22 * s, { face: 'display', fill: INK }), size / 2, size / 2 + 2 * s);
  const ink = mix(TILE.trajan.dark, 0x000000, 0.35);
  if (t.kind === 'plus2') {
    put(c, label('+2', 14 * s, { face: 'display', fill: 0x8b2e2e }), size / 2, size - 13 * s);
  } else if (t.kind === 'demand') {
    put(
      c,
      icon(t.icon ?? 'bread', 18 * s, DEMAND_INK[t.icon ?? 'bread'] ?? ink),
      size / 2,
      size - 13 * s,
    );
  } else {
    const g = put(
      c,
      icon(TRAJAN_ICON[t.kind] ?? 'star', 17 * s, ink),
      size / 2 - (t.tokens === 2 ? 7 * s : 0),
      size - 13 * s,
    );
    if (t.tokens === 2) put(c, icon(TRAJAN_ICON[t.kind] ?? 'star', 17 * s, ink), g.x + 14 * s, g.y);
  }
  return c;
}

const BONUS_ICON: Record<string, string> = {
  'per-display-card': 'cards',
  'per-worker-on-site': 'hammer',
  'per-legionnaire-in-province': 'military',
  'per-yellow-tile': 'star',
};

/** A consul's bonus tile: what it pays for, and its two rates. */
export function bonusFace(
  t: BonusTile,
  w: number,
  h: number,
  side: 'yellow' | 'grey' | 'both',
  caption: string,
): Container {
  const c = new Container();
  const p: Paint = side === 'grey' ? TILE.bonusGrey : TILE.bonusYellow;
  c.addChild(tile(w, h, p));
  const ink = mix(p.dark, 0x000000, 0.4);
  const k = h / 58;
  const glyph = t.kind === 'demand-icon' ? (t.icon ?? 'bread') : (BONUS_ICON[t.kind] ?? 'star');
  put(
    c,
    icon(glyph, 26 * k, t.kind === 'demand-icon' ? (DEMAND_INK[glyph] ?? ink) : ink),
    20 * k,
    h / 2,
  );
  put(
    c,
    label(caption, 10.5 * k, {
      fill: INK,
      weight: '700',
      anchor: [0, 0.5],
      wrap: w - 44 * k,
      align: 'left',
    }),
    38 * k,
    h / 2 - 6 * k,
  );
  const rates =
    side === 'both'
      ? `${t.yellow} / ${t.grey}`
      : side === 'yellow'
        ? `${t.yellow} each`
        : `${t.grey} each`;
  put(
    c,
    label(rates, 10 * k, { fill: INK_SOFT, weight: '700', anchor: [0, 0.5] }),
    38 * k,
    h / 2 + 12 * k,
  );
  return c;
}

/** A bonus tile in a player's keeping: its glyph and the rate its side pays; the words are in the tooltip. */
export function bonusChip(t: BonusTile, side: 'yellow' | 'grey', size: number): Container {
  const c = new Container();
  const p: Paint = side === 'grey' ? TILE.bonusGrey : TILE.bonusYellow;
  c.addChild(tile(size, size, p));
  const ink = mix(p.dark, 0x000000, 0.4);
  const glyph = t.kind === 'demand-icon' ? (t.icon ?? 'bread') : (BONUS_ICON[t.kind] ?? 'star');
  put(
    c,
    icon(glyph, size * 0.4, t.kind === 'demand-icon' ? (DEMAND_INK[glyph] ?? ink) : ink),
    size / 2,
    size * 0.34,
  );
  put(
    c,
    label(String(side === 'grey' ? t.grey : t.yellow), size * 0.3, { face: 'display', fill: INK }),
    size / 2,
    size * 0.73,
  );
  return c;
}

/** A commodity card, face up: its commodity's picture and name. */
export function commodityCard(
  card: Pick<CommodityCard, 'type'>,
  w: number,
  h: number,
  shadow = true,
): Container {
  const c = cardFace(w, h, shadow);
  const ink = COMMODITY_INK[card.type] ?? INK;
  const band = new Graphics().roundRect(4, 4, w - 8, h * 0.58, 3).fill({ color: ink, alpha: 0.12 });
  c.addChild(band);
  put(c, icon(card.type, Math.min(w, h) * 0.56, ink), w / 2, 4 + h * 0.29);
  put(c, label(card.type, Math.max(8, w * 0.16), { fill: INK, weight: '700' }), w / 2, h * 0.78);
  return c;
}

/** Each commodity keeps one colour everywhere, so sets are easy to see in a hand. */
export const COMMODITY_INK: Record<string, number> = {
  wheat: 0xa47a1c,
  fish: 0x2f6f8a,
  fruit: 0xb3352c,
  paper: 0x7a6a4a,
  jewelry: 0x3f7fa6,
  necklace: 0x9c3f8a,
  leather: 0x7a4a24,
  pottery: 0xb0602c,
  statue: 0x6b6f76,
  wine: 0x6b2a5c,
  cow: 0x4f3a2a,
  vase: 0x2f7a5c,
};
